import { zodResolver } from "@hookform/resolvers/zod";
import { BriefcaseBusiness, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { HideRetiredFilter } from "@/components/shared/hide-retired-filter";
import { ReorderButtons } from "@/components/shared/reorder-buttons";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  type EmploymentType,
  useCreateEmploymentType,
  useDeleteEmploymentType,
  useEmploymentTypes,
  useReorderEmploymentType,
  useUpdateEmploymentType,
} from "@/hooks/use-employment-types";
import { useRetiredRows } from "@/hooks/use-retired-rows";
import {
  type EmploymentTypeCreateInput,
  employmentTypeCreateSchema,
  isProtectedEmploymentTypeKey,
} from "@/schemas/employment-types";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; employmentType: EmploymentType }
  | { type: "delete"; employmentType: EmploymentType };

/** "40", "37.5", or an em-dash for a type with no weekly expectation. */
function formatHours(hours: number | null): string {
  if (hours === null) return "—";
  return String(hours);
}

export function SettingsEmploymentTypesPage() {
  const { data: types = [], isLoading, isError, error, refetch } = useEmploymentTypes();
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const deleteType = useDeleteEmploymentType();
  const reorder = useReorderEmploymentType();
  const { hideRetired, setHideRetired, active, hiddenCount, visible } = useRetiredRows(types);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load employment types"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The engagement basis a person is on, and the hours someone of that type is expected to work
        each week. Leave the hours blank when a type has no fixed weekly expectation — time is then
        recorded without being compared against a target. A person with their own agreed hours
        overrides this on their profile.
      </p>

      <DataToolbar
        filters={
          <HideRetiredFilter
            id="employment-types-hide-retired"
            checked={hideRetired}
            onCheckedChange={setHideRetired}
            hiddenCount={hiddenCount}
          />
        }
        actions={
          <Button onClick={() => setDialog({ type: "create" })}>
            <Plus className="mr-2 h-4 w-4" />
            Add type
          </Button>
        }
      />

      <DataTable
        headers={[
          { label: "Order", className: "w-24" },
          { label: "Name" },
          { label: "Expected hours / week" },
          { label: "People" },
          { label: "Status" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={BriefcaseBusiness}
            title="No employment types"
            description="Add the first employment type people can be assigned to."
          />
        }
      >
        {visible.map((t, index) => (
          <tr
            key={t.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3">
              {/* A retired row is pinned to the bottom by the API ordering, so its
                  position carries no meaning and it gets no arrows. The arrows on
                  the active rows above it are therefore always adjacent swaps. */}
              {t.isActive ? (
                <ReorderButtons
                  label={t.name}
                  disableUp={index === 0 || reorder.isPending}
                  disableDown={index === active.length - 1 || reorder.isPending}
                  onMove={(direction) => reorder.mutate({ id: t.id, direction })}
                />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className="px-4 py-3">
              <div className="font-medium">{t.name}</div>
              {/* The key is what the rest of the system stores and branches on,
                  so it is shown but never editable. */}
              <code className="text-xs text-muted-foreground">{t.key}</code>
            </td>
            <td className="px-4 py-3">
              {t.expectedWeeklyHours === null ? (
                <span className="text-muted-foreground">No expectation</span>
              ) : (
                formatHours(t.expectedWeeklyHours)
              )}
            </td>
            <td className="px-4 py-3 text-muted-foreground">{t.personCount}</td>
            <td className="px-4 py-3">
              <StatusPill
                tone={t.isActive ? "success" : "neutral"}
                label={t.isActive ? "Active" : "Inactive"}
              />
            </td>
            <td className="px-4 py-3 text-right">
              <ActionCell
                items={[
                  {
                    icon: Pencil,
                    label: "Edit",
                    onClick: () => setDialog({ type: "edit", employmentType: t }),
                  },
                  {
                    icon: Trash2,
                    label: "Delete",
                    onClick: () => setDialog({ type: "delete", employmentType: t }),
                    variant: "destructive",
                    separatorBefore: true,
                    // Built-in types and types anyone still holds can only be
                    // deactivated — the server refuses either way, but there is
                    // no point offering an action that can't succeed.
                    disabled: isProtectedEmploymentTypeKey(t.key) || t.personCount > 0,
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      {(dialog.type === "create" || dialog.type === "edit") && (
        <EmploymentTypeDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          employmentType={dialog.type === "edit" ? dialog.employmentType : undefined}
        />
      )}

      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          title={`Delete "${dialog.employmentType.name}"?`}
          description="This removes the type from the list. It can only be deleted while nobody is on it — otherwise deactivate it instead, which hides it from the pickers but keeps it readable on past records."
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            deleteType.mutate(dialog.employmentType.id, {
              onSuccess: () => setDialog({ type: "none" }),
            });
          }}
        />
      )}
    </div>
  );
}

function EmploymentTypeDialog({
  open,
  onOpenChange,
  employmentType,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employmentType?: EmploymentType;
}) {
  const isEdit = employmentType != null;
  const create = useCreateEmploymentType();
  const update = useUpdateEmploymentType(employmentType?.id ?? "");
  const form = useForm<EmploymentTypeCreateInput>({
    resolver: zodResolver(employmentTypeCreateSchema),
    defaultValues: {
      key: employmentType?.key ?? "",
      name: employmentType?.name ?? "",
      expectedWeeklyHours: employmentType?.expectedWeeklyHours ?? null,
      isActive: employmentType?.isActive ?? true,
    },
  });

  async function onSubmit(values: EmploymentTypeCreateInput) {
    if (isEdit) {
      // `key` is deliberately not sent: it is immutable once created, because
      // code branches on it and every past audit diff already stores it.
      await update.mutateAsync({
        name: values.name,
        expectedWeeklyHours: values.expectedWeeklyHours ?? null,
        isActive: values.isActive,
      });
    } else {
      await create.mutateAsync(values);
    }
    form.reset();
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "Edit employment type" : "Add employment type"}
      description={
        isEdit
          ? "Update the label, the expected weekly hours, or whether this type can still be chosen."
          : "Add a new engagement basis people can be assigned to."
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel={isEdit ? "Save" : "Create"}
      pending={pending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Part time" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="key"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Key</FormLabel>
            <FormControl>
              <Input placeholder="e.g. seasonal" {...field} disabled={isEdit} />
            </FormControl>
            <FormDescription>
              {isEdit
                ? "The key can't be changed — it's stored on every person's record and in their change history."
                : "A permanent identifier used internally. Lowercase letters, numbers and underscores."}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="expectedWeeklyHours"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Expected hours per week</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={0}
                max={168}
                step="0.5"
                placeholder="Leave blank for no expectation"
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(e.target.value === "" ? null : Number(e.target.value))
                }
              />
            </FormControl>
            <FormDescription>
              Blank means this type has no weekly target, so logged time isn't compared against one.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="isActive"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value ?? true} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Available for selection</FormLabel>
              <FormDescription>
                Turn this off to retire the type. It disappears from the pickers but keeps rendering
                in history and on anyone who still holds it.
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}
